import React, { ChangeEvent, useState } from 'react'
import type { Comment } from '../../types'
import { CHANNEL_WORDS } from '../../utils'
import { VisuallyHidden } from '../CardParts'
import styles from './feedback.module.sass'
import { ChannelDot } from './Quotes'
import { COMMENTS_PER_PAGE, matchComments, pageCount, pageOf, showingLabel } from './utils'

// Every comment, newest first as the server sends them, searchable and paged.
// No motion.
export const CommentsCard = ({ comments }: { comments: Comment[] }) => {
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(0)
  const matches = matchComments(comments, query)
  const pages = pageCount(matches.length)
  const current = Math.min(page, pages - 1)

  const onSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value)
    setPage(0)
  }
  const clearSearch = () => {
    setQuery('')
    setPage(0)
  }
  const goPrevious = () => setPage(current - 1)
  const goNext = () => setPage(current + 1)

  return (
    <article className={styles.commentsCard}>
      <div className={styles.commentsHeader}>
        <h3 className={styles.commentsTitle}>All comments</h3>
        <label className={styles.search}>
          <VisuallyHidden>Search comments</VisuallyHidden>
          <input type="search" className={styles.searchInput} placeholder="Search comments" value={query} onChange={onSearch} />
        </label>
      </div>
      <p className={styles.commentsCount}>{comments.length === 0 ? '' : showingLabel(current, matches.length)}</p>
      <CommentsTable comments={pageOf(matches, current)} />
      <NoComments total={comments.length} matches={matches.length} query={query} onClear={clearSearch} />
      <div className={styles.pager}>
        <span className={styles.smallPrint}>{`${COMMENTS_PER_PAGE} per page`}</span>
        <div className={styles.pagerButtons}>
          <button type="button" className={styles.pagerButton} onClick={goPrevious} disabled={current === 0}>Previous</button>
          <button type="button" className={styles.pagerButton} onClick={goNext} disabled={current >= pages - 1}>Next</button>
        </div>
      </div>
    </article>
  )
}

const CommentsTable = ({ comments }: { comments: Comment[] }) => {
  const rows = comments.map((comment) => <CommentRow key={comment.id} comment={comment} />)

  return (
    <table className={styles.comments}>
      <thead className={styles.commentsHead}>
        <tr>
          <th scope="col" className={styles.commentsTh}>Comment</th>
          <th scope="col" className={`${styles.commentsTh} ${styles.channelColumn}`}>Channel</th>
        </tr>
      </thead>
      <tbody>{rows}</tbody>
    </table>
  )
}

const CommentRow = ({ comment }: { comment: Comment }) => {
  const preparing = comment.state === 'preparing' || comment.text === null

  return (
    <tr className={styles.commentRow}>
      <td className={preparing ? `${styles.commentText} ${styles.commentPreparing}` : styles.commentText}>{preparing ? 'Comment being prepared' : comment.text}</td>
      <td className={`${styles.commentMeta} ${styles.channelColumn}`}>
        <span className={styles.commentChannel}>
          <ChannelDot channel={comment.channel} />
          {CHANNEL_WORDS[comment.channel].name}
        </span>
      </td>
    </tr>
  )
}

type NoCommentsProps = { total: number; matches: number; query: string; onClear: () => void }

const NoComments = ({ total, matches, query, onClear }: NoCommentsProps) => {
  if (total === 0) return <p className={styles.noComments}>No written comments yet</p>
  if (matches > 0) return null

  return (
    <div className={styles.noMatch}>
      <p className={styles.noComments}>{`No comments match ‘${query.trim()}’`}</p>
      <button type="button" className={styles.clearSearch} onClick={onClear}>Clear search</button>
    </div>
  )
}
